/**
 * Loads and assembles Site Effects partials for preview and 11ty export.
 * The fetch cache is shared so the first preview render and first export reuse
 * the same static partial text.
 */

import { SITE_EFFECT_DEFAULTS } from '../core/state.js';
import { getFeatureDefaults } from '../export/feature-registry.js';
import { loadAsset } from '../core/asset-loader.js';

export const SITE_EFFECT_PARTIALS = Object.freeze({
  darkModeJS: 'dark-mode.js',
  pressFeedbackJS: 'press-feedback.js',
  pressFeedbackCSS: 'press-feedback.css',
  themeWipeJS: 'theme-wipe.js',
  themeWipeCSS: 'theme-wipe.css',
  floatingLabelsCSS: 'floating-labels.css',
  goTopRingHTML: 'go-top-ring.html',
  goTopRingJS: 'go-top-ring.js',
  goTopRingCSS: 'go-top-ring.css',
  pageTransitionsCSS: 'page-transitions.css',
  hapticsJS: 'haptics.js',
  smoothScrollJS: 'smooth-scroll.js'
});

const SITE_EFFECT_BASE_PATH = './data/site-effects/';
const EMPTY_PARTIALS = Object.freeze(
  Object.fromEntries(Object.keys(SITE_EFFECT_PARTIALS).map((key) => [key, '']))
);

let partialCache = null;
let loadPromise = null;

function normalizeSiteEffects(state) {
  return { ...SITE_EFFECT_DEFAULTS, ...(state?.siteEffects || {}) };
}

function normalizeFeatures(state) {
  return { ...getFeatureDefaults(), ...(state?.features || {}) };
}

async function fetchPartial(key, filename) {
  try {
    const resp = await loadAsset(`data/site-effects/${filename}`);
    return [key, await resp.text()];
  } catch (err) {
    console.warn(`[SiteEffects] Could not load ${filename}:`, err);
    return [key, ''];
  }
}

function getPartial(key) {
  return partialCache?.[key] || '';
}

function joinPartials(parts) {
  return parts.filter(Boolean).join('\n\n');
}

export function loadSiteEffectPartials() {
  if (loadPromise) return loadPromise;
  loadPromise = Promise.all(
    Object.entries(SITE_EFFECT_PARTIALS).map(([key, filename]) => fetchPartial(key, filename))
  ).then((entries) => {
    partialCache = { ...EMPTY_PARTIALS, ...Object.fromEntries(entries) };
    return partialCache;
  });
  return loadPromise;
}

export function assemblePreviewSiteEffectsCSS(state) {
  const effects = normalizeSiteEffects(state);
  return joinPartials([
    getPartial('pressFeedbackCSS'),
    effects.themeWipe ? getPartial('themeWipeCSS') : '',
    effects.floatingLabels ? getPartial('floatingLabelsCSS') : '',
    effects.goTopRing ? getPartial('goTopRingCSS') : ''
  ]);
}

export function assemblePreviewSiteEffectsJS(state) {
  const effects = normalizeSiteEffects(state);
  return joinPartials([
    getPartial('darkModeJS'),
    getPartial('pressFeedbackJS'),
    effects.themeWipe ? getPartial('themeWipeJS') : '',
    effects.goTopRing ? getPartial('goTopRingJS') : '',
    effects.smoothScroll ? getPartial('smoothScrollJS') : ''
  ]);
}

export function assembleExportSiteEffectsCSS(state) {
  const effects = normalizeSiteEffects(state);
  const features = normalizeFeatures(state);
  return joinPartials([
    getPartial('pressFeedbackCSS'),
    effects.themeWipe ? getPartial('themeWipeCSS') : '',
    effects.floatingLabels ? getPartial('floatingLabelsCSS') : '',
    effects.goTopRing ? getPartial('goTopRingCSS') : '',
    features.pageViewTransitions ? getPartial('pageTransitionsCSS') : ''
  ]);
}

export function assembleExportSiteEffectsJS(state) {
  const effects = normalizeSiteEffects(state);
  const features = normalizeFeatures(state);
  return joinPartials([
    '/* Site Effects bundle */',
    getPartial('darkModeJS'),
    getPartial('pressFeedbackJS'),
    effects.themeWipe ? getPartial('themeWipeJS') : '',
    effects.goTopRing ? getPartial('goTopRingJS') : '',
    features.hapticFeedback ? getPartial('hapticsJS') : '',
    effects.smoothScroll ? getPartial('smoothScrollJS') : ''
  ]);
}

export function getGoTopRingHTML(state) {
  const effects = normalizeSiteEffects(state);
  return effects.goTopRing ? getPartial('goTopRingHTML') : '';
}

export function __resetSiteEffectPartialsForTests() {
  partialCache = null;
  loadPromise = null;
}

export function __getSiteEffectPartialCacheForTests() {
  return partialCache;
}
